import { useGetCandidates, useGetJobs, useGetTasks } from "openapi/api/endpoints"
import { taskId } from "@/lib/tasks"

export type PersonalViewData = {
    todaysTasks: {
        id?: number
        title?: string
        type?: "candidate" | "job" | "interview"
        priority?: "low" | "medium" | "high"
        due?: string
    }[]
    activeJobs?: {
        id: number
        title: string
        company: string
        status: "active" | "closing" | "paused"
    }[]
    leaderboard?: {
        rank: number
        name: string
        xp: number
        hires: number
        badge: string
    }[]
    stats?: {
        tasksDueToday: number
        highPriorityTasks: number
        activeJobsCount: number
        totalCandidates: number
        weeklyXP: number
        xpGrowth: string // e.g., "+12%"
        leaderboardRank: number
        totalRecruiters: number
    }
    xpProgress?: {
        current: number
        target: number
        currentLevel: number
        nextLevel: number
    }
}


export const usePersonalView = () => {
    const { data: jobs, isLoading: jobsLoading, error: jobsError } = useGetJobs()
    const { data: tasks, isLoading: tasksLoading, isError: tasksError } = useGetTasks()
    const { data: candidates, isLoading: candidatesLoading, isError: candidatesErr } = useGetCandidates()
    const errorStatus = tasksError || jobsError || candidatesErr
    const loadingStatus = tasksLoading || jobsLoading || candidatesLoading
    const candidatesCount = candidates?.candidates?.length ?? 0

    let personalViewData: PersonalViewData = {
        todaysTasks: tasks
            ?.filter((t) => t.status !== "done")
            .map((taskItem) => ({
            // Either name, as everywhere else: a row with only `id` otherwise
            // keyed and linked to `undefined`, and one with only `title` read
            // as untitled.
            id: taskId(taskItem),
            title:
                (taskItem as { task_title?: string }).task_title?.trim() ||
                taskItem.title?.trim() ||
                "Untitled Task",
            type: taskItem.entity_type === "job_candidate" ? "candidate" : "job",
            priority: taskItem.priority ?? "medium",
            due: taskItem.due_date ?? "N/A",
        })).slice(0,5) as PersonalViewData["todaysTasks"],

        activeJobs: jobs
            ?.map((jobItem) => ({
                id: jobItem.id,
                title: jobItem.title ?? "Untitled Role",
                company: jobItem.company_name ?? "Unknown Company",
                status: jobItem.status ?? "active",
            }))
            .filter((job) => job.status === "active") as PersonalViewData["activeJobs"],

        leaderboard: [
            { rank: 1, name: "Sarah Johnson", xp: 2450, hires: 8, badge: "🥇" },
            { rank: 2, name: "Mike Chen", xp: 2380, hires: 7, badge: "🥈" },
            { rank: 3, name: "Alex Rivera", xp: 2200, hires: 6, badge: "🥉" },
            { rank: 4, name: "You", xp: 1950, hires: 5, badge: "" },
            { rank: 5, name: "Emma Davis", xp: 1800, hires: 4, badge: "" },
        ],
    }

    const today = new Date().toISOString().split("T")[0]

    // ✅ now that personalViewData exists, you can safely use it
    personalViewData.stats = {
        tasksDueToday:tasks?.filter((t) => t.due_date === today).length ?? 0,
        highPriorityTasks:
            personalViewData.todaysTasks?.filter(
                (task) => task.priority === "high"
            ).length ?? 0,
        activeJobsCount: personalViewData.activeJobs?.length ?? 0,
        totalCandidates: candidatesCount,
        weeklyXP: 450,
        xpGrowth: "+12%",
        leaderboardRank:
            personalViewData?.leaderboard?.find((x) => x.name === "You")?.rank ?? 0,
        totalRecruiters: 12,
    }

    personalViewData.xpProgress = {
        current: personalViewData?.leaderboard?.find((x) => x.name === "You")?.xp ?? 0,
        target: 2500,
        currentLevel: 12,
        nextLevel: 13,
    }

    return {
        personalViewData,
        errorStatus,
        loadingStatus
    }

}
